import React, { useState } from "react";
import { Badge } from "../../../components/ui/badge";
import { Icons } from "../../../components/ui/icons";
import SegmentedControl from "../../../components/mobile/segmented-control";
import { DashboardActivity } from "../../../lib/api";
import { cn, getBadgeVariant } from "../../../lib/utils";
import { formatActivityDate, formatMoney, humanize } from "./format";
import { MobileCard, SectionHeader } from "./section";

type RecentTab = "quotations" | "invoices" | "movements";

interface RecentActivityCardProps {
  quotations: DashboardActivity[];
  invoices: DashboardActivity[];
  movements: DashboardActivity[];
}

const TAB_CONFIG: Record<RecentTab, { label: string; href: string; empty: string }> = {
  quotations: { label: "Quotes", href: "/quotes", empty: "No recent quotes" },
  invoices: { label: "Invoices", href: "/invoices", empty: "No recent invoices" },
  movements: { label: "Movements", href: "/activities", empty: "No recent movements" },
};

const StatusLine: React.FC<{ status: string; createdAt: string }> = ({ status, createdAt }) => (
  <div className="mt-1.5 flex items-center gap-2">
    <Badge variant={getBadgeVariant(status)} className="rounded-full px-2 py-0 text-[10px]">
      {humanize(status)}
    </Badge>
    <span className="text-[11px] text-gray-400">{formatActivityDate(createdAt)}</span>
  </div>
);

const DocumentRow: React.FC<{ activity: DashboardActivity }> = ({ activity }) => {
  const Icon = activity.type === "invoice" ? Icons.Billing : Icons.Quotes;
  const subtitle = [activity.customer, activity.site].filter(Boolean).join(" · ");
  return (
    <li className="flex items-start gap-3 py-3">
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary-50 text-primary-600">
        <Icon width={20} height={20} />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-3">
          <p className="truncate text-sm font-semibold text-gray-900">
            {activity.reference || "—"}
          </p>
          <p className="shrink-0 text-sm font-semibold text-gray-900">
            {formatMoney(activity.amount, activity.currencyType || "TZS")}
          </p>
        </div>
        {subtitle && <p className="truncate text-xs text-gray-500">{subtitle}</p>}
        <StatusLine status={activity.status} createdAt={activity.createdAt} />
      </div>
    </li>
  );
};

const MovementRow: React.FC<{ activity: DashboardActivity }> = ({ activity }) => {
  const isInflow = activity.activityType?.toUpperCase() === "INFLOW";
  return (
    <li className="flex items-start gap-3 py-3">
      <span
        className={cn(
          "flex h-10 w-10 shrink-0 items-center justify-center rounded-xl",
          isInflow ? "bg-emerald-50 text-emerald-600" : "bg-orange-50 text-orange-600",
        )}
      >
        <Icons.Movement width={20} height={20} />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-3">
          <p className="truncate text-sm font-semibold text-gray-900">
            {humanize(activity.activitySubType) || "Movement"}
          </p>
          <span
            className={cn(
              "shrink-0 text-[11px] font-semibold",
              isInflow ? "text-emerald-600" : "text-orange-600",
            )}
          >
            {humanize(activity.activityType)}
          </span>
        </div>
        <p className="truncate text-xs text-gray-500">
          {activity.source || "—"} → {activity.destination || "—"}
        </p>
        <StatusLine status={activity.status} createdAt={activity.createdAt} />
      </div>
    </li>
  );
};

const RecentActivityCard: React.FC<RecentActivityCardProps> = (lists) => {
  const firstWithData =
    (Object.keys(TAB_CONFIG) as RecentTab[]).find((key) => lists[key].length > 0) ??
    "quotations";
  const [selectedTab, setSelectedTab] = useState<RecentTab | null>(null);
  const tab = selectedTab ?? firstWithData;

  const items = lists[tab];
  const config = TAB_CONFIG[tab];

  return (
    <section>
      <SectionHeader title="Recent activity" actionHref={config.href} />
      <MobileCard>
        <SegmentedControl
          value={tab}
          onChange={setSelectedTab}
          options={(Object.keys(TAB_CONFIG) as RecentTab[]).map((key) => ({
            value: key,
            label: TAB_CONFIG[key].label,
            count: lists[key].length,
          }))}
        />
        {items.length > 0 ? (
          <ul className="mt-1 divide-y divide-gray-50">
            {items.map((activity) =>
              activity.type === "movement" ? (
                <MovementRow key={activity.id} activity={activity} />
              ) : (
                <DocumentRow key={activity.id} activity={activity} />
              ),
            )}
          </ul>
        ) : (
          <div className="flex flex-col items-center justify-center py-10 text-center">
            <Icons.Document width={28} height={28} className="text-gray-300" />
            <p className="mt-2 text-sm text-gray-500">{config.empty}</p>
          </div>
        )}
      </MobileCard>
    </section>
  );
};

export default RecentActivityCard;
